import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getPendingExpensesInvoices, updateExpensesInvoiceStatus } from '../../firebase/database';
import { generateExpensesInvoicePDF, openPDFInNewTab } from '../../services/invoicePdfService';
import { ExpensesInvoice } from '../../types';
import { formatCurrencyWithConversion } from '../../utils/number';
import toast, { Toaster } from 'react-hot-toast';

const ExpensesPendingApprovals: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [pendingInvoices, setPendingInvoices] = useState<ExpensesInvoice[]>([]);
  const [selectedInvoice, setSelectedInvoice] = useState<ExpensesInvoice | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    loadPendingInvoices();
  }, []);

  const loadPendingInvoices = async () => {
    try {
      setLoading(true);
      const invoices = await getPendingExpensesInvoices();
      setPendingInvoices(invoices as unknown as ExpensesInvoice[]);
    } catch (error) {
      console.error('Error loading pending expenses invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (invoice: ExpensesInvoice) => {
    setIsProcessing(true);
    try {
      await updateExpensesInvoiceStatus(invoice.id, 'approved', currentUser?.id);
      setPendingInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
      setSelectedInvoice(null);
      toast.success('Expenses invoice approved successfully!');
    } catch (error) {
      console.error('Error approving expenses invoice:', error);
      toast.error('Error approving invoice. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (invoice: ExpensesInvoice) => {
    setIsProcessing(true);
    try {
      await updateExpensesInvoiceStatus(invoice.id, 'rejected', currentUser?.id);
      setPendingInvoices(prev => prev.filter(inv => inv.id !== invoice.id));
      setSelectedInvoice(null);
      toast.success('Expenses invoice rejected successfully!');
    } catch (error) {
      console.error('Error rejecting expenses invoice:', error);
      toast.error('Error rejecting invoice. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewPDF = async (invoice: ExpensesInvoice) => {
    setIsGeneratingPDF(true);
    try {
      const pdf = await generateExpensesInvoicePDF(invoice);
      openPDFInNewTab(pdf);
    } catch (error) {
      console.error('Error generating expenses PDF:', error);
      toast.error('Error generating PDF. Please try again.');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleAmend = (invoice: ExpensesInvoice) => {
    navigate(`/edit-expenses-invoice/${invoice.id}`);
  };

  const formatDate = (date: Date) => date.toLocaleDateString();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pending expenses invoices...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 rounded-lg">
      <Toaster />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Pending Expenses Approvals</h1>
              <p className="mt-2 text-gray-600">Review and approve pending expenses invoices</p>
            </div>
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Back to Dashboard
            </button>
          </div>
        </div>

        {pendingInvoices.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8 text-center">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Pending Expenses Invoices</h3>
            <p className="text-gray-600">All expenses invoices have been processed.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="bg-white shadow rounded-lg">
                <div className="px-4 py-5 sm:p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    Pending Expenses ({pendingInvoices.length})
                  </h3>
                  <div className="space-y-3">
                    {pendingInvoices.map((invoice) => (
                      <div
                        key={invoice.id}
                        onClick={() => setSelectedInvoice(invoice)}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                          selectedInvoice?.id === invoice.id
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">{invoice.invoiceNumber}</h4>
                            <p className="text-sm text-gray-600">{invoice.clientName}</p>
                            <p className="text-xs text-gray-500">{formatDate(invoice.date)}</p>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">
                              {invoice.currency} {invoice.total.toFixed(2)}
                            </p>
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(invoice.status)}`}>
                              {invoice.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2">
              {selectedInvoice ? (
                <div className="bg-white shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-lg font-medium text-gray-900">
                        Expenses Invoice Details - {selectedInvoice.invoiceNumber}
                      </h3>
                      <button
                        onClick={() => handleViewPDF(selectedInvoice)}
                        disabled={isGeneratingPDF}
                        className="px-3 py-1 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md hover:bg-indigo-100 disabled:opacity-50"
                      >
                        {isGeneratingPDF ? 'Generating...' : 'View PDF'}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-3">Invoice Information</h4>
                        <dl className="space-y-2">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Invoice Number</dt>
                            <dd className="text-sm text-gray-900">{selectedInvoice.invoiceNumber}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Date</dt>
                            <dd className="text-sm text-gray-900">{formatDate(selectedInvoice.date)}</dd>
                          </div>
                        </dl>
                      </div>

                      <div className="mb-6">
                        <h4 className="font-medium text-gray-900 mb-3">Company Information</h4>
                        <dl className="space-y-2">
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Company Name</dt>
                            <dd className="text-sm text-gray-900">{selectedInvoice.clientName}</dd>
                          </div>
                          <div>
                            <dt className="text-sm font-medium text-gray-500">Logistics Company</dt>
                            <dd className="text-sm text-gray-900">{selectedInvoice.logisticsCompany}</dd>
                          </div>
                        </dl>
                      </div>
                    </div>

                    <div className="mb-6">
                      <h4 className="font-medium text-gray-900 mb-3">Invoice Items</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {selectedInvoice.items.map(item => (
                              <tr key={item.id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.description}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.quantity}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrencyWithConversion(item.rate, item.currency, selectedInvoice.exchangeRate)}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrencyWithConversion(item.amount, item.currency, selectedInvoice.exchangeRate)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="mb-6">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="text-sm text-gray-600">Subtotal: {selectedInvoice.currency} {selectedInvoice.subtotal.toFixed(2)}</p>
                            <p className="text-lg font-bold text-gray-900">Total: {selectedInvoice.currency} {selectedInvoice.total.toFixed(2)}</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex space-x-4">
                      <button
                        onClick={() => handleApprove(selectedInvoice)}
                        disabled={isProcessing}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleReject(selectedInvoice)}
                        disabled={isProcessing}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : 'Reject'}
                      </button>
                      <button
                        onClick={() => handleAmend(selectedInvoice)}
                        disabled={isProcessing}
                        className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : 'Amend'}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white shadow rounded-lg p-8 text-center">
                  <div className="text-gray-400 mb-4">
                    <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Select an Expenses Invoice</h3>
                  <p className="text-gray-600">Choose an invoice from the list to view details and take action.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpensesPendingApprovals;


